import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Load Times New Roman fonts into jsPDF
 */
const loadTimesFont = async (doc) => {
  try {
    const fontsToLoad = [
      { file: 'times.ttf', name: 'TimesNewRoman', style: 'normal' },
      { file: 'timesbd.ttf', name: 'TimesNewRoman', style: 'bold' },
      { file: 'timesi.ttf', name: 'TimesNewRoman', style: 'italic' }
    ];

    let successCount = 0;

    for (const font of fontsToLoad) {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/fonts/${font.file}`);
        if (!response.ok) {
          console.warn(`Font file not found: ${font.file}`);
          continue;
        }
        const fontBuffer = await response.arrayBuffer();
        const base64Font = arrayBufferToBase64(fontBuffer);
        doc.addFileToVFS(font.file, base64Font);
        doc.addFont(font.file, font.name, font.style);
        successCount++;
      } catch (err) {
        console.warn(`Failed to load ${font.file}:`, err);
      }
    }
    
    if (successCount === 3) {
      doc.setFont('TimesNewRoman', 'normal');
      return true;
    } else {
      doc.setFont('helvetica', 'normal');
      return false;
    }
  } catch (error) {
    console.error('Error loading Times New Roman fonts:', error);
    doc.setFont('helvetica', 'normal');
    return false;
  }
};

/**
 * Convert ArrayBuffer to Base64
 */
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

/**
 * Load image and convert to base64
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (error) => {
      console.error('Failed to load image:', imagePath, error);
      reject(error);
    };
    img.src = `${process.env.PUBLIC_URL}${imagePath}`;
  });
};

/**
 * Parse HTML content and render with formatting in PDF
 * Supports bold, italic, line breaks, paragraphs, and lists
 */
const renderHtmlContent = (doc, html, x, startY, maxWidth, fontFamily) => {
  let yPos = startY;
  const lineHeight = 4.5; // Reduced from 5 for more compact spacing
  const pageHeight = 297;
  const margin = 15;
  const baseFontSize = 10; // Base font size for body text
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Process each child node
  const processNode = (node, currentStyle = { bold: false, italic: false }) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Text node - render with current style
      const text = node.textContent.trim();
      if (text) {
        // Set font style based on current formatting
        const fontStyle = currentStyle.bold && currentStyle.italic ? 'bolditalic' :
                         currentStyle.bold ? 'bold' :
                         currentStyle.italic ? 'italic' : 'normal';
        doc.setFont(fontFamily, fontStyle);
        doc.setFontSize(baseFontSize); // Ensure consistent font size
        
        // Split text to fit width
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          // Check if we need a new page
          if (yPos > pageHeight - 40) {
            doc.addPage();
            yPos = margin;
          }
          doc.text(line, x, yPos);
          yPos += lineHeight;
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      let newStyle = { ...currentStyle };
      
      // Update style based on tag
      if (tagName === 'strong' || tagName === 'b') {
        newStyle.bold = true;
      } else if (tagName === 'em' || tagName === 'i') {
        newStyle.italic = true;
      } else if (tagName === 'p') {
        // No extra spacing for paragraphs - ReactQuill wraps each line in <p>
        // Just process the content normally
      } else if (tagName === 'br') {
        // Line break
        yPos += lineHeight;
        return;
      } else if (tagName === 'ul' || tagName === 'ol') {
        // List - add tiny spacing before only if not at start
        if (yPos !== startY) {
          yPos += 0.5;
        }
      } else if (tagName === 'li') {
        // List item - add bullet/number
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }
        doc.setFont(fontFamily, 'normal');
        doc.setFontSize(baseFontSize);
        doc.text('•', x, yPos);
        
        // Process list item content with indent
        const listItemDiv = document.createElement('div');
        listItemDiv.innerHTML = node.innerHTML;
        const listItemText = listItemDiv.textContent.trim();
        
        if (listItemText) {
          const lines = doc.splitTextToSize(listItemText, maxWidth - 10);
          lines.forEach((line, index) => {
            if (yPos > pageHeight - 40) {
              doc.addPage();
              yPos = margin;
            }
            doc.text(line, x + 5, yPos);
            if (index < lines.length - 1) {
              yPos += lineHeight;
            }
          });
        }
        yPos += lineHeight;
        return;
      }
      
      // Process child nodes
      Array.from(node.childNodes).forEach(child => {
        processNode(child, newStyle);
      });
    }
  };
  
  // Process all nodes
  Array.from(tempDiv.childNodes).forEach(node => {
    processNode(node);
  });
  
  return yPos;
};

/**
 * Split text into lines that fit within maxWidth
 */
const splitTextToFitWidth = (doc, text, maxWidth) => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines;
};

/**
 * Generate Decision Letter PDF
 * @param {Object} data - Letter data
 * @param {string} data.applicationNo - Application number
 * @param {string} data.letterDate - Letter date
 * @param {string} data.contactPerson - Contact person name
 * @param {string} data.laboratoryName - Laboratory name
 * @param {string} data.address - Address
 * @param {string} data.extAddress - Extended address
 * @param {string} data.mobile - Mobile number
 * @param {string} data.email - Email
 * @param {string} data.subject - Letter subject
 * @param {string} data.body - Letter body (HTML)
 * @param {Array} data.advisoryList - Array of advisory items
 * @param {string} data.doName - DO name
 * @param {string} data.doEmail - DO email
 * @param {boolean} data.showAdverseNote - Show adverse decision note
 * @param {boolean} data.showNewAddress - Use new NABL address
 */
export const generateDecisionLetter = async (data) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const fontsLoaded = await loadTimesFont(doc);
  const fontFamily = fontsLoaded ? 'TimesNewRoman' : 'helvetica';

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);

  try {
    // Load header images (three logos)
    let ilacImage, nablLogoImage, qciLogoImage;

    try {
      ilacImage = await loadImageAsBase64('/certificate_images/ilac-MRA.jpg');
      console.log('✓ ILAC image loaded');
    } catch (err) {
      console.warn('ILAC image not found: ilac-MRA.jpg');
    }

    try {
      nablLogoImage = await loadImageAsBase64('/certificate_images/Logo.JPG');
      console.log('✓ NABL Logo image loaded');
    } catch (err) {
      console.warn('NABL Logo image not found: Logo.JPG');
    }

    try {
      qciLogoImage = await loadImageAsBase64('/certificate_images/QCI-logo.jpg');
      console.log('✓ QCI Logo image loaded');
    } catch (err) {
      console.warn('QCI Logo image not found: QCI-logo.jpg');
    }

    let yPos = 15;

    // Add header with three logos and text
    const logoHeight = 25;
    const logoWidth = 20;

    // Left logo (ILAC)
    if (ilacImage) {
      doc.addImage(ilacImage, 'JPEG', margin, yPos, logoWidth, logoHeight);
    }

    // Center-left logo (NABL)
    if (nablLogoImage) {
      doc.addImage(nablLogoImage, 'JPEG', margin + logoWidth + 3, yPos, logoWidth, logoHeight);
    }

    // Right logo (QCI)
    if (qciLogoImage) {
      doc.addImage(qciLogoImage, 'JPEG', pageWidth - margin - logoWidth, yPos, logoWidth, logoHeight);
    }

    // Header text (center) - positioned to avoid logos
    const centerX = pageWidth / 2;
    const textStartX = margin + logoWidth * 2 + 8; // After left logos with padding
    const textEndX = pageWidth - margin - logoWidth - 5; // Before right logo with padding
    const textMaxWidth = textEndX - textStartX;
    
    doc.setFont(fontFamily, 'bold');
    doc.setFontSize(13);
    doc.setTextColor(82, 61, 168); // Purple color #523da8
    const headerLines = doc.splitTextToSize('National Accreditation Board for Testing and Calibration Laboratories', textMaxWidth);
    let textY = yPos + 8;
    headerLines.forEach(line => {
      doc.text(line, centerX, textY, { align: 'center' });
      textY += 5;
    });
    
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('(A Constituent Board of Quality Council of India)', centerX, textY + 2, { align: 'center', maxWidth: textMaxWidth });

    doc.setTextColor(0, 0, 0); // Reset to black
    yPos += logoHeight + 10;

    // Application number and date
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(10);
    doc.text(data.applicationNo || '', margin, yPos);
    doc.text(data.letterDate || '', pageWidth - margin - 40, yPos, { align: 'right' });

    yPos += 10;

    // Contact person name (bold)
    doc.setFont(fontFamily, 'bold');
    doc.text(data.contactPerson || '', margin, yPos);
    yPos += 5;

    // Laboratory name
    doc.setFont(fontFamily, 'normal');
    doc.text(data.laboratoryName || '', margin, yPos);
    yPos += 5;

    // Address
    const addressLines = doc.splitTextToSize(data.address || '', contentWidth * 0.6);
    addressLines.forEach(line => {
      doc.text(line, margin, yPos);
      yPos += 5;
    });

    // Extended address
    if (data.extAddress) {
      const extAddressLines = doc.splitTextToSize(data.extAddress, contentWidth * 0.6);
      extAddressLines.forEach(line => {
        doc.text(line, margin, yPos);
        yPos += 5;
      });
    }

    // Mobile
    doc.text(`Mobile: ${data.mobile || ''}`, margin, yPos);
    yPos += 5;

    // Email
    doc.text(`E-mail: ${data.email || ''}`, margin, yPos);
    yPos += 10;

    // Subject (bold)
    doc.setFont(fontFamily, 'bold');
    const subjectText = `Subject: ${data.subject || ''}`;
    const subjectLines = doc.splitTextToSize(subjectText, contentWidth);
    subjectLines.forEach(line => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
      }
      doc.text(line, margin, yPos);
      yPos += 5;
    });

    yPos += 5;

    // Greeting
    doc.setFont(fontFamily, 'normal');
    doc.setFontSize(10);
    doc.text('Dear Sir/Ma\'am,', margin, yPos);
    yPos += 10;

    // Body content (render HTML with formatting) - Set font size before rendering
    doc.setFontSize(10);
    yPos = renderHtmlContent(doc, data.body || '', margin, yPos, contentWidth, fontFamily);

    yPos += 5;

    // Adverse decision note (if applicable)
    if (data.showAdverseNote) {
      if (yPos > pageHeight - 50) {
        doc.addPage();
        yPos = margin;
      }
      const noteText = 'In case you are not satisfied with the decision, you have an option to appeal against the decision. Please refer NABL 134 in this regard. Please refer NABL 216 for re-enrolment / restoration procedure.';
      const noteLines = doc.splitTextToSize(noteText, contentWidth);
      noteLines.forEach(line => {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      });
      yPos += 5;
    }

    // Closing
    doc.text('Yours faithfully,', margin, yPos);
    yPos += 10;

    // DO name and email
    doc.setFont(fontFamily, 'bold');
    doc.text(data.doName || '', margin, yPos);
    yPos += 5;
    doc.setFont(fontFamily, 'normal');
    doc.text(data.doEmail || '', margin, yPos);

    // Footer on last page
    const footerY = pageHeight - 20;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    
    if (data.showNewAddress) {
      doc.text('J200, World Trade Centre, Nauroji Nagar 110029, New Delhi, India', pageWidth / 2, footerY, { align: 'center' });
      doc.text('Website: www.nabl-india.org', pageWidth / 2, footerY + 5, { align: 'center' });
    } else {
      doc.text('NABL House, Plot 45, Sector 44, Gurugram 122 002, Haryana, India', pageWidth / 2, footerY, { align: 'center' });
      doc.text('Tel. No.: +91-124-4679700 (30 lines) * Fax: +91-124-4679799 * Website: www.nabl-india.org', pageWidth / 2, footerY + 5, { align: 'center' });
    }

    // Generate filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `Decision_Letter_${data.applicationNo}_${timestamp}.pdf`;

    // Save PDF
    doc.save(filename);

    return { success: true, filename };

  } catch (error) {
    console.error('Error generating decision letter:', error);
    throw error;
  }
};

export default generateDecisionLetter;
